import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createAdditionalReportList(
    $data: [createAdditionalReportInput]
  ) {
    createAdditionalReportList(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createAdditionalReportListRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data?: TYPES.CreateAdditionalReportInput[];},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'additional_report/create_additional_report_list',
  async (
  params: {data?: TYPES.CreateAdditionalReportInput[];},
  { extra: client }) =>
  {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createAdditionalReportList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createAdditionalReportListSlice = createSlice({
  name: 'create_additional_report_list',
  initialState: initialState,
  reducers: {
    createAdditionalReportListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createAdditionalReportListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      createAdditionalReportListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      createAdditionalReportListRequest.rejected,
      (state, action) => {
        state.error = action.payload as Error || {
          message: 'Failed to fetch',
          statusCode: '500'
        };
        state.loading = false;
      }
    );
  }
});

export const { createAdditionalReportListClear } =
createAdditionalReportListSlice.actions;

export const createAdditionalReportList = createSelector(
  (state: RootState) =>
  state.additionalReport.createAdditionalReportList.result,
  (state: RootState) =>
  state.additionalReport.createAdditionalReportList.error,
  (state: RootState) =>
  state.additionalReport.createAdditionalReportList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createAdditionalReportListSlice.reducer;